import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { GeolocationCoords } from 'app/types/common';
import classNames from 'classnames';
import { useGetWarehousesForBookingQuery, WarehouseForBooking } from 'entities/Warehouse';
import { YandexMap, YandexMarker, useYandexMapRoute } from 'features/YandexMap';
import { useSpecialPromotionModal } from 'features/SpecialPromotion';
import { FC, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
import { Button } from 'shared/ui/Button';
import { useGeolocation } from 'shared/utils/hooks/useGeolocation';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { getOutlinedCard } from 'widgets/SelectWarehouseMap/model/selectors/getOutlinedCard';
import { getSearch } from 'widgets/SelectWarehouseMap/model/selectors/getSearch';
import { getSelectedCard } from 'widgets/SelectWarehouseMap/model/selectors/getSelectedCard';
import { warehouseMapActions } from 'widgets/SelectWarehouseMap/model/slice/warehouseMapSlice';
import { calculateDistance } from 'widgets/SelectWarehouseMap/utils/helpers/calculateDistance';
import { SelectWarehouseMapMobileYandex } from './SelectWarehouseMapMobileYandex';
import { ModernWarehousesList } from 'features/WarehousesList';
import { getWarehouseInfo } from 'pages/BookingPage/model/selectors/getWarehouseInfo';
import { formatPrice } from 'shared/utils/helpers/formatPrice';

interface SelectWarehouseMapYandexProps {
  onCompleteStep: (warehouse: WarehouseForBooking) => void;
}

export const SelectWarehouseMapYandex: FC<SelectWarehouseMapYandexProps> = memo((props) => {
  const { onCompleteStep } = props;

  const { t } = useAppTranslation(['booking', 'common', 'login-page']);
  const { routeResult, calculateRoute, resetRoute } = useYandexMapRoute();
  const currencySymbol = useGetCurrencySymbol();
  const { showSpecialPromotion } = useSpecialPromotionModal();
  const userCoords = useGeolocation();

  const dispatch = useAppDispatch();

  const selectedWarehouseCard = useAppSelector(getSelectedCard);
  const outlinedWarehouseCard = useAppSelector(getOutlinedCard);
  const search = useAppSelector(getSearch);
  const { data } = useGetWarehousesForBookingQuery();

  // Get the selected warehouse from booking state
  const selectedWarehouseFromBooking = useAppSelector(getWarehouseInfo);

  const selectedWarehouseData = data?.find((warehouse) => warehouse.warehouseId === selectedWarehouseCard);

  const [center, setCenter] = useState<Nullable<GeolocationCoords>>(null);

  const { isDesktop } = useScreenBreakpoints();

  // Sync selected warehouse from booking state with warehouse map state
  useEffect(() => {
    if (selectedWarehouseFromBooking && !selectedWarehouseCard) {
      dispatch(warehouseMapActions.setSelectedCard(selectedWarehouseFromBooking.warehouseId));
    }
  }, [selectedWarehouseFromBooking, selectedWarehouseCard, dispatch]);

  const onSelectWarehouseCard = useCallback(
    (warehouseCoords: GeolocationCoords): void => {
      // Add small offset to make warehouse card not overlap the marker in mobile version
      const verticalOffset = 0.003;

      setCenter(!isDesktop ? { ...warehouseCoords, lat: warehouseCoords.lat - verticalOffset } : warehouseCoords);
    },
    [isDesktop],
  );

  const warehousesWithCoordinates = useMemo(
    () =>
      data
        ?.filter((warehouse) => warehouse.longitude && warehouse.latitude)
        .sort((a, b) => {
          if (userCoords) {
            const distanceA = calculateDistance(userCoords.lat, userCoords.lng, a.latitude, a.longitude);
            const distanceB = calculateDistance(userCoords.lat, userCoords.lng, b.latitude, b.longitude);

            return distanceA - distanceB;
          }

          return 0;
        }),
    [data, userCoords],
  );

  const drawRoute = useCallback(
    async (destination: GeolocationCoords, warehouseId: string): Promise<void> => {
      const calculatedRoute = await calculateRoute(destination);

      if (calculatedRoute) {
        dispatch(warehouseMapActions.setRoutedWarehouseId(warehouseId));
      }
    },
    [calculateRoute, dispatch],
  );

  const clearRoute = useCallback((): void => {
    resetRoute();
    dispatch(warehouseMapActions.setRoutedWarehouseId(null));
  }, [dispatch, resetRoute]);

  const filteredBySearch = useMemo<WarehouseForBooking[] | undefined>(() => {
    return search
      ? warehousesWithCoordinates?.filter(({ name, cityName }) => {
          const searchValue = search.toLowerCase();
          const searchByName = name.toLowerCase().includes(searchValue);
          const searchByCity = cityName.toLowerCase().includes(searchValue);

          return searchByName || searchByCity;
        })
      : warehousesWithCoordinates;
  }, [warehousesWithCoordinates, search]);

  useEffect(() => {
    if (filteredBySearch?.length) {
      const defaultWarehouseCoords: GeolocationCoords = {
        lat: filteredBySearch[0].latitude,
        lng: filteredBySearch[0].longitude,
      };

      setCenter(defaultWarehouseCoords);
    }
  }, [filteredBySearch]);

  const markers = useMemo<YandexMarker[]>(() => {
    return (
      filteredBySearch?.map((warehouse) => {
        const getLabelDescription = (): ReactNode => {
          const onBookNow = (): void => {
            onCompleteStep(warehouse);
          };

          return (
            <>
              <Trans
                t={t}
                i18nKey="From <0>{{minPrice}}{{currencySymbol}}</0>"
                components={[<span key="0" className="text-accent" />]}
                values={{ minPrice: formatPrice(warehouse.minPrice), currencySymbol }}
              />
              <Button className="mt-3.5" onClick={onBookNow}>
                {t('View boxes')}
              </Button>
            </>
          );
        };

        return {
          id: warehouse.warehouseId,
          position: { lat: warehouse.latitude, lng: warehouse.longitude },
          labelHeading: warehouse.name,
          labelDescription: getLabelDescription(),
          warehouseData: warehouse,
        };
      }) || []
    );
  }, [currencySymbol, filteredBySearch, onCompleteStep, t]);

  const selectMarker = useCallback(
    (lat: number, lng: number, markerId: string): void => {
      dispatch(warehouseMapActions.setSelectedCard(markerId));
    },
    [dispatch],
  );

  const selectWarehouseAndProceed = useCallback(
    (markerId: string): void => {
      dispatch(warehouseMapActions.setSelectedCard(markerId));

      // Find the warehouse data and proceed to next step
      const selectedWarehouse = data?.find((warehouse) => warehouse.warehouseId === markerId);
      if (selectedWarehouse) {
        onCompleteStep(selectedWarehouse);
      }
    },
    [dispatch, data, onCompleteStep],
  );

  return (
    <div className="absolute left-0 right-0 bottom-0 top-[110px] desktop:static">
      <div className="desktop:hidden pt-4">
        <SelectWarehouseMapMobileYandex
          selectedWarehouseId={selectedWarehouseCard}
          outlinedWarehouseId={outlinedWarehouseCard}
          selectMarker={selectMarker}
          onGetRoute={drawRoute}
          onClearRoute={clearRoute}
          selectedWarehouseData={selectedWarehouseData}
          onSelectMarker={onSelectWarehouseCard}
          initialWarehousesCount={data?.length || 0}
          warehouses={filteredBySearch}
          selectedMarkerId={selectedWarehouseCard}
          markers={markers}
          center={center}
          routeResult={routeResult}
          onCompleteStep={onCompleteStep}
        />
      </div>
      <div
        className={classNames(
          'hidden absolute desktop:block bg-white rounded-md p-[10px]',
          { 'top-[calc(theme(spacing.headerHeight)+50px)]': !showSpecialPromotion },
          { 'top-[calc(theme(spacing.headerHeight)+116px)]': showSpecialPromotion },
        )}
        style={{
          left: '50px',
          right: '50px',
          bottom: '25px',
        }}
      >
        <YandexMap
          mapContainerClassName="w-full h-full rounded-md overflow-hidden"
          markers={markers}
          center={center}
          mapId="warehouseMapYandex"
          selectedMarkerId={selectedWarehouseCard}
          selectMarker={selectMarker}
          routeResult={routeResult}
          onSelectMarker={onSelectWarehouseCard}
          withMarkerPopups
          onSelectWarehouseAndProceed={selectWarehouseAndProceed}
        >
          <ModernWarehousesList
            warehouses={filteredBySearch}
            outlinedWarehouseId={outlinedWarehouseCard}
            onSelectCard={selectMarker}
            onGetRoute={drawRoute}
            onClearRoute={clearRoute}
            onSelectWarehouse={onCompleteStep}
          />
          {routeResult && (
            <Button
              containerClassName="absolute top-2 right-1/4 -translate-x-1/2"
              theme="secondary"
              icon={<CloseIcon className="stroke-primaryLight" />}
              iconPosition="prev"
              onClick={clearRoute}
            >
              {t('Cancel', { ns: 'common' })}
            </Button>
          )}
        </YandexMap>
      </div>
    </div>
  );
});
